import React, { useState } from 'react';
import {
  Calendar,
  CheckCircle,
  AlertTriangle,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Users, Mail } from 'lucide-react';
import StatCard from './StatCard';
import CalendarConnectionsPanel from './CalendarConnectionsPanel';
import HubSpotConnection from './HubSpotConnection';
import SalesforceConnection from './SalesforceConnection';
import styles from './HomeView.module.css';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function HomeView({
  stats,
  calendarConnections,
  calendarConnection,
  calendarRefreshKey,
  syncingProviders,
  syncing,
  userId,
  userName,
  onSyncProvider,
  onSyncAll,
  onConnectGoogle,
  onConnectMicrosoft,
  onConnectApple,
  onDisconnectProvider,
  onShowCalendarSelector,
  onFindMatches,
  findingMatches,
}) {
  const [crmExpanded, setCrmExpanded] = useState(false);

  const unhealthyConnections = (calendarConnections || []).filter(c => c.health && c.health !== 'ok');

  const displayName = userName || 'there';

  return (
    <div>
      {/* Greeting */}
      <h1 className={styles.greeting}>{getGreeting()}, {displayName}</h1>

      {/* Quick actions */}
      <div className={styles.quickActions}>
        <button
          className={styles.findMatchesBtn}
          onClick={onFindMatches}
          disabled={findingMatches || !calendarConnection}
        >
          <Search size={16} className={findingMatches ? styles.pulseIcon : undefined} />
          {findingMatches ? 'Finding...' : 'Find Matches'}
        </button>
      </div>

      {/* Calendar health warnings */}
      {unhealthyConnections.map(conn => (
        <div key={conn.provider} className={styles.warningCard}>
          <AlertTriangle size={18} className={styles.warningIcon} />
          <p className={styles.warningText}>
            <span className={styles.warningProvider}>
              {conn.provider === 'google' ? 'Google Calendar' : 'Microsoft 365'}
            </span>
            {' — '}
            {conn.token_status?.message || (conn.health === 'error' ? 'Reconnection required' : 'Attention needed')}
          </p>
        </div>
      ))}

      {/* Stats */}
      <div className={styles.statsGrid}>
        <StatCard icon={Users}       label="Opted-In Customers"   value={stats?.optedInCustomers || 0}     color="blue" />
        <StatCard icon={Mail}        label="Pending Offers"        value={stats?.pendingOffers || 0}        color="yellow" />
        <StatCard icon={Calendar}    label="Upcoming Appointments" value={stats?.upcomingAppointments || 0} color="green" />
        <StatCard icon={CheckCircle} label="Total Bookings"        value={stats?.totalAcceptedOffers || 0}  color="purple" />
      </div>

      {/* Calendar Connections — collapsed by default when healthy */}
      <div className={styles.section}>
        <CalendarConnectionsPanel
          calendarConnections={calendarConnections}
          syncingProviders={syncingProviders}
          syncing={syncing}
          onSyncProvider={onSyncProvider}
          onSyncAll={onSyncAll}
          onConnectGoogle={onConnectGoogle}
          onConnectMicrosoft={onConnectMicrosoft}
          onConnectApple={onConnectApple}
          onDisconnectProvider={onDisconnectProvider}
          onShowCalendarSelector={onShowCalendarSelector}
        />
      </div>

      {/* CRM Integrations — collapsible */}
      <div className={styles.section}>
        <button
          className={styles.collapsibleHeader}
          onClick={() => setCrmExpanded(prev => !prev)}
        >
          <h2 className={styles.sectionTitle}>CRM Integrations</h2>
          {crmExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        {crmExpanded && (
          <div className={styles.crmGrid}>
            <HubSpotConnection userId={userId} />
            <SalesforceConnection />
          </div>
        )}
      </div>
    </div>
  );
}
